import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { ArrowUpRight } from 'lucide-react';

export default function Contact() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', company: '', enquiry: '' });
  const [sending, setSending] = useState(false);

  const onChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async e => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.enquiry) {
      toast({ title: 'Please complete the required fields.', description: 'First name, email and enquiry details are required.' });
      return;
    }
    setSending(true);
    // Frontend-only mock submission — stored to localStorage for now.
    try {
      const stored = JSON.parse(localStorage.getItem('fgc_enquiries') || '[]');
      stored.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('fgc_enquiries', JSON.stringify(stored));
      await new Promise(r => setTimeout(r, 600));
      toast({ title: 'Enquiry received.', description: 'Thank you — Hannah will be in touch shortly.' });
      setForm({ firstName: '', lastName: '', email: '', company: '', enquiry: '' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-36 bg-[#2E2C28] text-[#EBE4DE]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="sec-num text-sm tracking-[0.3em] uppercase" style={{color:'#D8AE94'}} data-reveal>/ 09</p>
          <p className="mt-6 text-[13px] tracking-[0.24em] uppercase text-[#c9c0b3]" data-reveal style={{ '--reveal-delay': '80ms' }}>Start a conversation</p>
          <h2 className="mt-6 font-serif text-[40px] lg:text-[60px] leading-[1.02] tracking-[-0.015em] font-light" data-reveal style={{ '--reveal-delay': '160ms' }}>
            Ready to scale <span className="italic" style={{color:'#D8AE94'}}>past yourself</span>?
          </h2>
          <p className="mt-8 text-lg text-[#c9c0b3] font-light leading-relaxed max-w-md" data-reveal style={{ '--reveal-delay': '280ms' }}>
            Book a discovery session with Hannah. We’ll explore where founder-led sales is holding you back and what a structured sales function could look like at your stage.
          </p>

          <div className="mt-14 space-y-6 text-sm" data-reveal style={{ '--reveal-delay': '360ms' }}>
            <ContactLine label="Email" value="hannah@foundergrowthcollective.co.uk" href="mailto:hannah@foundergrowthcollective.co.uk" />
            <ContactLine label="LinkedIn" value="Hannah Hulley" href="https://www.linkedin.com" />
            <ContactLine label="Location" value="Sheffield, UK — Europe Time Zone" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="First name *" name="firstName" value={form.firstName} onChange={onChange} />
              <Field label="Last name" name="lastName" value={form.lastName} onChange={onChange} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} />
              <Field label="Company name" name="company" value={form.company} onChange={onChange} />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.22em] uppercase text-[#c9c0b3] mb-3">Enquiry details *</label>
              <Textarea name="enquiry" value={form.enquiry} onChange={onChange} rows={6} className="bg-transparent border-0 border-b border-[#6b6459] focus-visible:ring-0 focus-visible:border-[#D8AE94] rounded-none px-0 text-[#EBE4DE] placeholder:text-[#7a7369] text-base font-light" placeholder="Tell us about your business, stage and where sales currently sits..." />
            </div>
            <div className="pt-4">
              <Button type="submit" disabled={sending} className="bg-[#EBE4DE] hover:bg-[#B5876C] hover:text-[#EBE4DE] text-[#2E2C28] rounded-none h-14 px-8 text-[12px] tracking-[0.2em] uppercase font-medium transition-colors duration-500">
                <span className="inline-flex items-center gap-3">
                  {sending ? 'Sending…' : 'Submit Enquiry'}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#c9c0b3] mb-3">{label}</label>
      <Input {...props} className="bg-transparent border-0 border-b border-[#6b6459] focus-visible:ring-0 focus-visible:border-[#D8AE94] rounded-none px-0 text-[#EBE4DE] placeholder:text-[#7a7369] text-base font-light h-11" />
    </div>
  );
}

function ContactLine({ label, value, href }) {
  const Wrap = href ? 'a' : 'div';
  return (
    <div>
      <p className="text-[11px] tracking-[0.22em] uppercase text-[#7a7369] mb-2">{label}</p>
      <Wrap href={href} className={`font-serif text-lg text-[#EBE4DE] ${href ? 'hover:text-[#D8AE94] transition-colors' : ''}`}>
        {value}
      </Wrap>
    </div>
  );
}
